import { MantineColor } from "@mantine/core";

/**
 * Travel type values for tax categorization.
 * Must match backend TRAVEL_TYPE values in TravelService.
 */
export const TRAVEL_TYPES = {
  BUSINESS: "business",
  PERSONAL: "personal",
  MEDICAL: "medical",
  CHARITY: "charity",
  COMMUTE: "commute",
} as const;


export type TravelTypeValue = (typeof TRAVEL_TYPES)[keyof typeof TRAVEL_TYPES];

/**
 * Configuration for each travel type including icon, color, and translation key.
 */
export interface TravelTypeConfig {
  value: TravelTypeValue;
  /** Icon name for AppIcon component (without "Icon" prefix) */
  icon: string;
  color: MantineColor;
  translationKey: string;
}

export const DEDUCTIBLE_TRAVEL_TYPES: TravelTypeValue[] = [
  TRAVEL_TYPES.BUSINESS,
  TRAVEL_TYPES.MEDICAL,
  TRAVEL_TYPES.CHARITY,
];

/**
 * Travel type configurations mapped by value.
 */
export const TRAVEL_TYPE_CONFIG: Record<TravelTypeValue, TravelTypeConfig> = {
  [TRAVEL_TYPES.BUSINESS]: {
    value: TRAVEL_TYPES.BUSINESS,
    icon: "Briefcase",
    color: "blue",
    translationKey: "travels:travelTypes.business",
  },
  [TRAVEL_TYPES.PERSONAL]: {
    value: TRAVEL_TYPES.PERSONAL,
    icon: "User",
    color: "gray",
    translationKey: "travels:travelTypes.personal",
  },
  [TRAVEL_TYPES.MEDICAL]: {
    value: TRAVEL_TYPES.MEDICAL,
    icon: "FirstAidKit",
    color: "red",
    translationKey: "travels:travelTypes.medical",
  },
  [TRAVEL_TYPES.CHARITY]: {
    value: TRAVEL_TYPES.CHARITY,
    icon: "Heart",
    color: "pink",
    translationKey: "travels:travelTypes.charity",
  },
  [TRAVEL_TYPES.COMMUTE]: {
    value: TRAVEL_TYPES.COMMUTE,
    icon: "Building",
    color: "teal",
    translationKey: "travels:travelTypes.commute",
  },
};

/**
 * Get travel type configuration by value.
 * Returns undefined if value is not a valid travel type.
 */
export const getTravelTypeConfig = (
  value: string | undefined | null
): TravelTypeConfig | undefined => {
  if (!value) return undefined;
  return TRAVEL_TYPE_CONFIG[value as TravelTypeValue];
};

/**
 * Check if a travel type is tax-deductible
 */
export const isTravelTypeDeductible = (travelType: string): boolean => {
  return DEDUCTIBLE_TRAVEL_TYPES.includes(travelType as TravelTypeValue);
};

/**
 * Array of all travel type configurations for iteration.
 */
export const TRAVEL_TYPE_OPTIONS: TravelTypeConfig[] = Object.values(TRAVEL_TYPE_CONFIG);

/**
 * Default travel type for new travels.
 */
export const DEFAULT_TRAVEL_TYPE: TravelTypeValue = TRAVEL_TYPES.BUSINESS;